import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Delete, Edit, Check, Close } from '@mui/icons-material';
import { Comment, CommentScopeType, CommentVisibility } from '../../types';
import { BudgetApiService } from '../../services/budgetApiService';

interface CommentThreadProps {
  budgetId: number;
  versionId: number;
  scopeType: CommentScopeType;
  // Requerido para 'ambiente' (el id del ambiente); omitir para
  // 'mano_de_obra'/'flete' - esas dos secciones son únicas por versión
  // (CLAUDE.md §10 field report #11).
  scopeId?: number;
  // Lista COMPLETA de comentarios de la versión (sin filtrar) - este
  // componente filtra por su propio scope internamente, así el padre no
  // tiene que separar la lista por sección.
  comments: Comment[];
  // Solo mientras la versión sea content-editable (draft/pending_approval/
  // approved) - una vez enviada, el backend rechaza cualquier operación
  // (400), así que ocultamos el formulario de agregar/editar/eliminar en
  // vez de dejar que el usuario choque contra un error esperado.
  editable: boolean;
  // El padre mantiene la lista completa (formData.currentVersion.comments) -
  // este callback le devuelve la lista actualizada tras crear/editar/eliminar.
  onChange: (comments: Comment[]) => void;
  title?: string;
}

/**
 * Hilo de comentarios de una sección (CLAUDE.md §10 field report #11):
 * internos (visibles para usuario + admin, nunca el cliente) o
 * client_visible (imprimen en el PDF debajo de su sección). Cada
 * ambiente tiene el suyo; Mano de Obra y Flete tienen uno cada uno, no por
 * instancia.
 */
export const CommentThread: React.FC<CommentThreadProps> = ({
  budgetId,
  versionId,
  scopeType,
  scopeId,
  comments,
  editable,
  onChange,
  title,
}) => {
  const [newText, setNewText] = useState('');
  const [newVisibility, setNewVisibility] = useState<CommentVisibility>('internal');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const threadComments = comments.filter(
    (c) => c.scope_type === scopeType && (c.scope_id ?? null) === (scopeId ?? null)
  );

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const created = await BudgetApiService.createComment(budgetId, versionId, {
        scope_type: scopeType,
        scope_id: scopeId,
        visibility: newVisibility,
        text: newText.trim(),
      });
      onChange([...comments, created]);
      setNewText('');
      setNewVisibility('internal');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'No se pudo agregar el comentario');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await BudgetApiService.updateComment(id, { text: editingText });
      onChange(comments.map((c) => (c.id === id ? updated : c)));
      setEditingId(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'No se pudo editar el comentario');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      await BudgetApiService.deleteComment(id);
      onChange(comments.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'No se pudo eliminar el comentario');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px dashed #cfd8dc', borderRadius: 1, backgroundColor: '#fafafa' }}>
      <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
        💬 {title || 'Comentarios'}
      </Typography>
      <Stack spacing={0.75}>
        {threadComments.length === 0 && (
          <Typography variant="caption" color="text.disabled">
            Sin comentarios todavía
          </Typography>
        )}
        {threadComments.map((comment) => (
          <Box key={comment.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Chip
              size="small"
              label={comment.visibility === 'client_visible' ? 'Cliente' : 'Interno'}
              color={comment.visibility === 'client_visible' ? 'success' : 'default'}
              sx={{ mt: 0.25, flexShrink: 0 }}
            />
            {editingId === comment.id ? (
              <>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                />
                <IconButton size="small" onClick={() => handleSaveEdit(comment.id)} disabled={busy || !editingText.trim()}>
                  <Check fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setEditingId(null)} disabled={busy}>
                  <Close fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                  {comment.text}
                </Typography>
                {editable && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditingText(comment.text);
                      }}
                      disabled={busy}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(comment.id)} disabled={busy}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </>
                )}
              </>
            )}
          </Box>
        ))}
      </Stack>
      {editable && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={newVisibility}
            onChange={(_, v) => v && setNewVisibility(v)}
          >
            <ToggleButton value="internal">Interno</ToggleButton>
            <ToggleButton value="client_visible">Cliente</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            sx={{ flex: 1, minWidth: 180 }}
            placeholder="Agregar comentario..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
          />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={busy || !newText.trim()}>
            Agregar
          </Button>
        </Box>
      )}
      {error && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CommentThread;
